import React, { useEffect, useState } from 'react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import { useIntegrationStatus } from '../../contexts/IntegrationStatusContext';

export interface OrgGitLabConnectionItem {
  gitlab_instance_url: string;
  gitlab_username?: string;
  gitlab_email?: string;
  connected_at?: string;
  avatar_url?: string;
  web_url?: string;
  connected_by?: string;
}

export interface OrgGitLabStatus {
  connected: boolean;
  connections: OrgGitLabConnectionItem[];
  gitlab_username?: string;
  gitlab_email?: string;
  connected_at?: string;
  avatar_url?: string;
  web_url?: string;
  connected_by?: string;
}

interface OrgGitLabConnectionProps {
  organizationId: string;
  readOnly?: boolean;
}

function instanceDisplayName(url: string): string {
  try {
    const u = new URL(url);
    return u.hostname || url;
  } catch {
    return url;
  }
}

const OrgGitLabConnection: React.FC<OrgGitLabConnectionProps> = ({ organizationId, readOnly = false }) => {
  const api = useAuthenticatedApi();
  const { refresh: refreshIntegrationStatus } = useIntegrationStatus();
  const [status, setStatus] = useState<OrgGitLabStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnectInstanceUrl, setDisconnectInstanceUrl] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showSelfHosted, setShowSelfHosted] = useState(false);
  const [oauthForm, setOauthForm] = useState({ instance_url: '', oauth_application_id: '', oauth_application_secret: '' });
  const [selfHostedSubmitting, setSelfHostedSubmitting] = useState(false);
  const [selfHostedError, setSelfHostedError] = useState<string | null>(null);

  const refresh = async () => {
    try {
      const res = await api.get<OrgGitLabStatus>(`/organizations/${organizationId}/gitlab/status`);
      setStatus(res);
    } catch (e) {
      console.error('Failed to fetch org GitLab status', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [organizationId]);

  const connectGitLabCom = async () => {
    setConnecting(true);
    setSelfHostedError(null);
    try {
      const res = await api.get<{ authorization_url: string }>(`/organizations/${organizationId}/gitlab/connect`);
      window.location.href = res.authorization_url;
    } catch (e) {
      console.error('Failed to initiate org GitLab connection', e);
      setConnecting(false);
    }
  };

  const connectSelfHostedOAuth = async () => {
    setSelfHostedError(null);
    if (!oauthForm.instance_url.trim() || !oauthForm.oauth_application_id.trim() || !oauthForm.oauth_application_secret.trim()) {
      setSelfHostedError('Instance URL, Application ID, and Secret are required.');
      return;
    }
    setSelfHostedSubmitting(true);
    try {
      const res = await api.post<{ authorization_url: string }>(
        apiEndpoints.orgGitlab.connectSelfHostedOAuth(organizationId),
        {
          instance_url: oauthForm.instance_url.trim(),
          oauth_application_id: oauthForm.oauth_application_id.trim(),
          oauth_application_secret: oauthForm.oauth_application_secret.trim(),
        }
      );
      window.location.href = res.authorization_url;
    } catch (e: unknown) {
      const message = e && typeof e === 'object' && 'message' in e ? String((e as { message: string }).message) : 'Failed to start self-hosted OAuth';
      setSelfHostedError(message);
      setSelfHostedSubmitting(false);
    }
  };

  const openDisconnectModal = (instanceUrl: string | null) => {
    setDisconnectInstanceUrl(instanceUrl);
    setShowDisconnectModal(true);
  };

  const confirmDisconnect = async () => {
    setDisconnecting(true);
    try {
      const url = apiEndpoints.orgGitlab.disconnect(organizationId, disconnectInstanceUrl ?? undefined);
      await api.delete(url);
      refreshIntegrationStatus();
      await refresh();
      setShowDisconnectModal(false);
      setDisconnectInstanceUrl(null);
    } catch (e) {
      console.error('Failed to disconnect org GitLab', e);
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-ui-background-tertiary rounded w-3/4 mb-3"></div>
        <div className="h-10 bg-ui-background-tertiary rounded w-32"></div>
      </div>
    );
  }

  const connections = status?.connections ?? [];
  const hasConnections = status?.connected && connections.length > 0;

  if (!hasConnections) {
    return (
      <div className="space-y-3">
        <div className="text-sm text-ui-text-secondary">
          Connect a GitLab account to your organization to enable repository imports,
          commit changes, and merge request creation for all members.
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={connectGitLabCom}
            disabled={connecting || readOnly}
            className="px-4 py-2 rounded-lg transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed bg-brand-primary hover:bg-brand-primaryDark text-black"
          >
            {connecting ? 'Connecting…' : 'Connect GitLab.com'}
          </button>
          <button
            type="button"
            onClick={() => setShowSelfHosted(!showSelfHosted)}
            className="px-4 py-2 rounded-lg border border-ui-border-primary bg-ui-background-secondary text-ui-text-primary hover:bg-ui-background-tertiary transition-colors"
          >
            {showSelfHosted ? 'Hide self-hosted' : 'Connect self-hosted GitLab'}
          </button>
        </div>

        {showSelfHosted && (
          <div className="mt-4 p-4 bg-ui-background-tertiary rounded-lg border border-ui-border-primary space-y-4">
            {selfHostedError && (
              <p className="text-sm text-red-600 dark:text-red-400">{selfHostedError}</p>
            )}
            <div className="grid gap-2 max-w-md">
              <input
                type="url"
                placeholder="Instance URL (e.g. https://gitlab.mycompany.com)"
                value={oauthForm.instance_url}
                onChange={(e) => setOauthForm((f) => ({ ...f, instance_url: e.target.value }))}
                className="w-full px-3 py-2 rounded border border-ui-border-primary bg-ui-background-secondary text-ui-text-primary text-sm"
              />
              <input
                type="text"
                placeholder="OAuth Application ID"
                value={oauthForm.oauth_application_id}
                onChange={(e) => setOauthForm((f) => ({ ...f, oauth_application_id: e.target.value }))}
                className="w-full px-3 py-2 rounded border border-ui-border-primary bg-ui-background-secondary text-ui-text-primary text-sm"
              />
              <input
                type="password"
                placeholder="OAuth Application Secret"
                value={oauthForm.oauth_application_secret}
                onChange={(e) => setOauthForm((f) => ({ ...f, oauth_application_secret: e.target.value }))}
                className="w-full px-3 py-2 rounded border border-ui-border-primary bg-ui-background-secondary text-ui-text-primary text-sm"
              />
              <button
                type="button"
                onClick={connectSelfHostedOAuth}
                disabled={selfHostedSubmitting || readOnly}
                className="px-4 py-2 rounded-lg bg-brand-primary hover:bg-brand-primaryDark text-black disabled:opacity-60 disabled:cursor-not-allowed text-sm font-medium"
              >
                {selfHostedSubmitting ? 'Redirecting…' : 'Connect via OAuth'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {connections.map((conn) => (
          <div
            key={conn.gitlab_instance_url}
            className="bg-ui-background-tertiary border border-ui-border-primary rounded-lg p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {conn.avatar_url && (
                  <img
                    src={conn.avatar_url}
                    alt="avatar"
                    className="w-10 h-10 rounded-full border-2 border-ui-border-primary"
                  />
                )}
                <div>
                  <div className="font-medium text-ui-text-primary">
                    {instanceDisplayName(conn.gitlab_instance_url)} — @{conn.gitlab_username}
                  </div>
                  {conn.gitlab_email && (
                    <div className="text-xs text-ui-text-secondary">
                      {conn.gitlab_email}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {conn.web_url && (
                  <a
                    href={conn.web_url}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-2 rounded-lg bg-ui-background-tertiary text-ui-text-primary hover:bg-ui-background-tertiary transition-colors text-sm font-medium"
                  >
                    View Profile
                  </a>
                )}
                <button
                  onClick={() => openDisconnectModal(conn.gitlab_instance_url)}
                  disabled={readOnly}
                  className="px-3 py-2 rounded-lg transition-colors text-sm font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-red-100 dark:disabled:hover:bg-red-900/30"
                >
                  Disconnect
                </button>
              </div>
            </div>
          </div>
        ))}
        {connections.length > 1 && (
          <button
            onClick={() => openDisconnectModal(null)}
            disabled={readOnly}
            className="text-sm text-red-600 dark:text-red-400 hover:underline disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:no-underline"
          >
            Disconnect all GitLab connections
          </button>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={connectGitLabCom}
            disabled={connecting || readOnly}
            className="px-4 py-2 rounded-lg transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed bg-ui-background-tertiary text-ui-text-primary hover:bg-ui-background-tertiary text-sm"
          >
            {connecting ? 'Connecting…' : 'Add GitLab.com'}
          </button>
          <button
            type="button"
            onClick={() => setShowSelfHosted(!showSelfHosted)}
            className="px-4 py-2 rounded-lg border border-ui-border-primary bg-ui-background-secondary text-ui-text-primary hover:bg-ui-background-tertiary text-sm transition-colors"
          >
            {showSelfHosted ? 'Hide' : 'Add self-hosted GitLab'}
          </button>
        </div>
        {showSelfHosted && (
          <div className="mt-2 p-4 bg-ui-background-tertiary rounded-lg border border-ui-border-primary space-y-4">
            {selfHostedError && (
              <p className="text-sm text-red-600 dark:text-red-400">{selfHostedError}</p>
            )}
            <div className="grid gap-2 max-w-md">
              <input
                type="url"
                placeholder="Instance URL (e.g. https://gitlab.mycompany.com)"
                value={oauthForm.instance_url}
                onChange={(e) => setOauthForm((f) => ({ ...f, instance_url: e.target.value }))}
                className="w-full px-3 py-2 rounded border border-ui-border-primary bg-ui-background-secondary text-ui-text-primary text-sm"
              />
              <input
                type="text"
                placeholder="OAuth Application ID"
                value={oauthForm.oauth_application_id}
                onChange={(e) => setOauthForm((f) => ({ ...f, oauth_application_id: e.target.value }))}
                className="w-full px-3 py-2 rounded border border-ui-border-primary bg-ui-background-secondary text-ui-text-primary text-sm"
              />
              <input
                type="password"
                placeholder="OAuth Application Secret"
                value={oauthForm.oauth_application_secret}
                onChange={(e) => setOauthForm((f) => ({ ...f, oauth_application_secret: e.target.value }))}
                className="w-full px-3 py-2 rounded border border-ui-border-primary bg-ui-background-secondary text-ui-text-primary text-sm"
              />
              <button
                type="button"
                onClick={connectSelfHostedOAuth}
                disabled={selfHostedSubmitting || readOnly}
                className="px-4 py-2 rounded-lg bg-brand-primary hover:bg-brand-primaryDark text-black disabled:opacity-60 disabled:cursor-not-allowed text-sm font-medium"
              >
                {selfHostedSubmitting ? 'Redirecting…' : 'Connect via OAuth'}
              </button>
            </div>
          </div>
        )}
      </div>

      {showDisconnectModal && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => !disconnecting && setShowDisconnectModal(false)}
          />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div
              role="dialog"
              aria-modal="true"
              className="w-full max-w-md bg-ui-background-secondary rounded-xl shadow-2xl border border-ui-border-primary overflow-hidden"
            >
              <div className="px-5 py-4 border-b border-ui-border-primary">
                <h3 className="text-base font-semibold text-ui-text-primary">
                  Disconnect GitLab
                </h3>
              </div>
              <div className="px-5 py-4 text-sm text-ui-text-secondary">
                {disconnectInstanceUrl
                  ? `Disconnect ${instanceDisplayName(disconnectInstanceUrl)} from this organization?`
                  : 'Disconnect all GitLab connections? All members will lose access to GitLab features until reconnected.'}
              </div>
              <div className="px-5 py-4 flex justify-end gap-2 border-t border-ui-border-primary">
                <button
                  className="px-4 py-2 rounded-lg bg-ui-background-tertiary text-ui-text-primary hover:bg-ui-background-tertiary transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={() => setShowDisconnectModal(false)}
                  disabled={disconnecting}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={confirmDisconnect}
                  disabled={disconnecting}
                >
                  {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrgGitLabConnection;
